import { Immutable } from "immer";
import React from "react";
import { t } from "ttag";

import type { AdvertSnippetBlock as Value } from "@reactivated";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { getLinkUrl } from "../../utils/wagtail";

import bannerStyles from "../../styles/composable/banner.module.scss";

type IProps = Immutable<{
    value: Value;
}>;

type IAdvertChild = NonNullable<Immutable<Value>["advert"]>["body"][number];

const renderAdvertChild = (block: IAdvertChild): React.JSX.Element => {
    switch (block.type) {
        case "title":
            return (
                <h3 className={bannerStyles.bannerTitle} key={block.id}>
                    {block.value}
                </h3>
            );
        case "description":
            return (
                <p className={bannerStyles.bannerCopy} key={block.id}>
                    {block.value}
                </p>
            );
        case "cta":
            // rel="noreferrer" is a deliberate (visually inert) addition
            // over the legacy markup: eslint react/jsx-no-target-blank
            // requires it on target="_blank" links.
            return (
                <a
                    key={block.id}
                    className={concatClassNames([
                        bannerStyles.bannerCta,
                        "button",
                    ])}
                    href={getLinkUrl(block.value)}
                    target="_blank"
                    rel="noreferrer"
                    title={block.value.html_title ?? ""}
                >
                    {block.value.display_name}
                </a>
            );
    }
};

/**
 * Faithful JSX transcription of the legacy sealy
 * ``components/adverts/advert_snippet.html`` (+ its title/description/cta
 * child templates) (#33285). ``advert`` is null only when the (required)
 * SnippetChooserBlock resolves no snippet; the legacy template then emitted
 * an empty ``section.banner`` shell, which this skips entirely (nothing
 * visible either way).
 */
export const AdvertSnippetBlock = (props: IProps): React.JSX.Element | null => {
    const advert = props.value.advert;
    if (!advert) {
        return null;
    }
    return (
        <section
            className={concatClassNames([
                bannerStyles.banner,
                bannerStyles.bannerReverse,
                "ptb",
            ])}
            id="cocoon"
        >
            <figure className={bannerStyles.bannerFigure}>
                <img
                    className="img-responsive"
                    src={advert.image_url ?? ""}
                    alt={t`Cocoon by Sealy`}
                    loading="lazy"
                    decoding="async"
                />
            </figure>
            <div className={bannerStyles.bannerContent}>
                {advert.body.map(renderAdvertChild)}
            </div>
        </section>
    );
};
